/**
 * Tipos del dominio "Solicitud de actualización de datos".
 * Ver: docsmd/PLAN-FRONT-SOLICITUD-ACTUALIZACION-DATOS.md
 *
 * Los shapes exactos aún no fueron confirmados con backend; este archivo
 * fija el contrato esperado por el front. Cuando backend cierre el shape,
 * ajustar los campos opcionales aquí.
 */

export type RequestEstado =
  | 'PENDING'
  | 'COMPLETED'
  | 'EXPIRED'
  | 'CANCELLED'
  | 'CLOSED_MAX_REJECTS';

export type ItemStatus = 'PENDING' | 'SUBMITTED' | 'APPROVED' | 'REJECTED';

export type FieldType =
  | 'text'
  | 'number'
  | 'date'
  | 'boolean'
  | 'email'
  | 'file'
  | 'phonePrefix';

export type NumeroIdMatch = 'none' | 'usuario' | 'pagador';

/**
 * Resumen reactivo de bloqueo que consume el guard + interceptor.
 * Se persiste en un BehaviorSubject del service. Cualquier solicitud
 * abierta hace `bloqueado = true`.
 */
export interface BlockStatus {
  bloqueado: boolean;
  vencida: boolean;
  pendientes: number;
  requestId: string | null;
}

/**
 * Un item dentro de una solicitud — un campo específico a actualizar.
 *
 * El backend real devuelve el identificador como `id`. Mantenemos
 * `itemId` como alias legacy (algún endpoint o iteración anterior
 * podría usarlo). Los consumidores deben resolver el id vía
 * `getItemId(item)` para cubrir ambos shapes sin sorpresas.
 */
export interface DataUpdateRequestItem {
  /** Identificador del item — nombre canónico del backend. */
  id?: string;
  /** Alias legacy. Los consumidores usan `getItemId()` para leer ambos. */
  itemId?: string;
  fieldKey: string;
  fieldType: FieldType;
  /** Label humanizado opcional. Si no viene, el front mapea fieldKey → label. */
  label?: string;
  status: ItemStatus;
  required?: boolean;

  /**
   * Email del operador que revisó este item (APPROVED/REJECTED). `null`
   * si todavía nadie lo revisó. Reemplaza al legacy `reviewedByCognitoId`
   * que traía un UUID ilegible — ahora el backend expone el email
   * directamente para poder mostrarlo en la UI sin lookups.
   */
  reviewedByEmail?: string | null;
  /** Legacy — todavía aceptamos por compat, pero preferimos `reviewedByEmail`. */
  reviewedByCognitoId?: string | null;

  /** Valor actual en la BD (lo que el cliente cambia). null para archivos. */
  currentValue?: string | number | boolean | null;
  /** Valor enviado por el cliente. null hasta el submit. */
  submittedValue?: string | number | boolean | null;
  /** Motivo de rechazo si status === 'REJECTED'. */
  rejectReason?: string | null;

  // ── Archivos ──────────────────────────────────────────────────────
  /** Key en S3 del archivo subido (post file-confirm). Nombre real backend. */
  submittedS3Key?: string | null;
  /** Alias del front — se sigue leyendo con fallback. */
  fileKey?: string | null;
  /** URL pre-firmada GET para previsualizar. TTL ~1h. Nombre real backend. */
  previewUrl?: string | null;
  /** Alias del front — se lee con fallback. */
  fileSignedUrl?: string | null;
  /** ISO 8601 — momento en que expira la URL pre-firmada. */
  fileSignedUrlExpiresAt?: string | null;

  // ── Indicadores backend ──────────────────────────────────────────
  /** Datos extraídos por OCR (para items de documento de identidad). */
  ocrExtracted?: {
    nombres?: string | null;
    apellidos?: string | null;
    fechaNacimiento?: string | null;
    numeroId?: string | null;
  } | null;
  /** OCR detectó nombre/numeroId distinto al actual → revisar identidad. Backend lo devuelve como 0/1. */
  identityChange?: boolean | 0 | 1;
  /** ¿El numero_id propuesto ya existe en otro usuario o pagador? */
  numeroIdMatch?: NumeroIdMatch;

  /**
   * Resultado de la verificación facial Rekognition (selfie ↔ documento).
   *
   * Aparece **sólo** en el item `selfie` y **sólo** en respuestas del
   * operador (`GET /:id`, `GET /user/:usuarioId`). NO viene en las
   * respuestas del cliente (`/me`, `/me/:id`) — el cliente no debe
   * intentar leerlo.
   *
   * `null` cuando todavía no corrió (falta subir el selfie o la foto
   * frontal para que el comparador tenga con qué). Es informativo: el
   * operador decide si aprueba o rechaza; no bloquea por sí solo.
   */
  facialVerification?: FacialVerification | null;
}

/**
 * Resultado de la comparación facial diferida que corre server-side
 * cuando el cliente ya subió selfie + foto frontal. Sólo la ve el
 * operador en el detalle.
 */
export interface FacialVerification {
  /** True si la similitud pasa el `threshold`. */
  isMatch: boolean;
  /** Porcentaje 0–100. */
  similarity: number;
  /** Umbral configurado (0–100). */
  threshold: number;
  /** ¿Se detectó rostro en la selfie del cliente? */
  sourceFaceDetected: boolean;
  /** ¿Se detectó rostro en el documento (típico: foto frontal)? */
  targetFaceDetected: boolean;
  /** Con qué item se comparó — normalmente `'fotoIdfrontal'`. */
  comparedWith?: string;
  /** ISO 8601 — momento en que corrió la verificación. */
  checkedAt?: string;
}

/**
 * Helper para leer el id del item cubriendo ambos shapes que puede
 * devolver backend (`id` canónico o `itemId` legacy).
 */
export function getItemId(item: DataUpdateRequestItem): string {
  return (item?.id ?? item?.itemId ?? '') as string;
}

/**
 * Helper para leer la URL pre-firmada del archivo actual. Backend real
 * usa `previewUrl`; el front histórico usaba `fileSignedUrl`.
 */
export function getItemPreviewUrl(item: DataUpdateRequestItem): string | null {
  return (item?.previewUrl ?? item?.fileSignedUrl ?? null) as string | null;
}

/**
 * Detalle completo de una solicitud (operador o cliente dueño).
 */
export interface DataUpdateRequest {
  id: string;
  usuarioId: number;
  estado: RequestEstado;
  motivo: string;
  deadlineAt: string;
  vencida: boolean;
  rejectCount: number;
  /** Persona reportada por chequeo de listas (PEP/ONU). */
  riskReported: boolean;
  riskDetail?: string | null;
  items: DataUpdateRequestItem[];
  createdAt: string;
  updatedAt: string;
  /**
   * Email del operador que creó la solicitud. Reemplaza al legacy
   * `createdByCognitoId` (UUID ilegible) — ahora el backend expone
   * el email directamente.
   */
  createdByEmail?: string | null;
  /** Legacy — mantenido por compat con endpoints que aún no migraron. */
  createdByCognitoId?: string | null;
}

/**
 * Respuesta de `POST /data-update-requests/:id/items/:itemId/file-upload-url`.
 */
export interface UploadUrlResponse {
  url: string;
  key: string;
  expiresAt: string;
}

/**
 * Respuesta de `GET /data-update-requests/me`.
 *
 * Shape real del backend (con sub-objeto `block`):
 *
 *     {
 *       request: { id, estado, items: [...], ... },
 *       block: { bloqueado, vencida, pendientes }
 *     }
 *
 * También aceptamos el shape "plano" con los campos en el root
 * (`{ bloqueado, vencida, pendientes, requestId }`) por compatibilidad
 * con cualquier endpoint legacy o nueva iteración del contrato. El
 * front normaliza ambos en `DataUpdateRequestService.refresh()`.
 */
export interface MeStatusResponse {
  // Shape anidado real (preferido)
  request?: DataUpdateRequest | null;
  block?: {
    bloqueado: boolean;
    vencida: boolean;
    pendientes: number;
  } | null;

  // Shape plano legacy (fallback)
  bloqueado?: boolean;
  vencida?: boolean;
  pendientes?: number;
  requestId?: string | null;
}

// ── Operador ─────────────────────────────────────────────────────────

/**
 * Fila de la tabla de usuarios para crear una solicitud nueva.
 * `GET /data-update-requests/users`.
 *
 * Nota sobre `estadoCompliance*`: distintos endpoints del proyecto
 * exponen el estado de compliance con shapes distintos (legacy
 * `estadoComplianceID`, ID plano `estadoComplianceId`, string
 * `'APROBADO' | 'POR APROBAR' | …`, u objeto anidado
 * `estadoCompliance.estadoID`). Mantenemos los campos opcionales y la
 * lógica de resolución vive en el componente — ver `resolveCompliance()`
 * en `CreateDataUpdateRequestComponent`.
 *
 * Los `currentValues` son valores actuales que el operador previsualiza
 * antes de marcar el checkbox para pedirlos al cliente. Campo `fieldKey`
 * → valor actual (texto). Si el backend aún no los expone, el front
 * muestra "sin dato" inline. Las URLs pre-firmadas de archivos
 * (`fotoIdfrontal`, etc.) viven en `currentDocumentUrls` cuando estén
 * disponibles; mientras tanto el front pinta placeholder.
 */
export interface UserForRequest {
  usuarioId: number;
  email: string;
  nombre: string;
  apellidos: string;
  numeroId: string;

  /**
   * Fecha de alta del usuario en la plataforma (ISO 8601). El backend
   * la expone junto con los filtros `createdFrom` / `createdTo` — hasta
   * que backend la incluya en el response, el front la trata como
   * opcional y no la muestra si falta.
   */
  createdAt?: string;
  updatedAt?: string;

  /**
   * Tipo de usuario. El backend devuelve `tipoUsuarioID` (ID en mayúscula)
   * en `GET /data-update-requests/users`; aceptamos también `tipoUsuarioId`
   * camelCase por compatibilidad con otros endpoints.
   */
  tipoUsuarioId?: 0 | 1;
  tipoUsuarioID?: 0 | 1;

  /** ID numérico nuevo del backend (`3 = aprobado`). */
  estadoComplianceId?: number;
  /** Alias legacy en CamelCase. */
  estadoComplianceID?: number;
  /** String label (`'APROBADO' | 'POR APROBAR' | 'RECHAZADO' | …`) u objeto anidado. */
  estadoCompliance?: string | { estadoID?: number } | null;
  /** Si ya tiene una solicitud abierta, la incluye para avisar al operador. */
  activeRequestId?: string | null;

  // ── Campos de texto que el backend devuelve en el ROOT del usuario ──
  // El front los lee directo de aquí cuando `currentValues` no viene
  // poblado. Lista no exhaustiva — el front también prueba lecturas
  // dinámicas por `fieldKey` para no atarse a los campos hardcodeados.
  tipoId?: string;
  telefono?: string;
  prefijoTelefono?: string;
  direccion?: string;
  profesion?: string;
  paisResidencia?: string;
  ciudadResidencia?: string;
  nacionalidad?: string;
  fechaNacimiento?: string;
  fechaExp?: string | null;
  sexo?: string;
  esPoliticamenteExpuesto?: boolean;
  razonSocial?: string | null;
  rutEmpresa?: string | null;
  nombreComercial?: string | null;
  domicilioFiscal?: string | null;

  // ── Documentos como KEYS de S3 (firmar con S3UrlService) ──
  fotoIdfrontal?: string;
  fotoIdPosterior?: string;
  selfie?: string;
  estatuto?: string;
  documentoIdTributario?: string;

  /**
   * Sub-objeto opcional con valores actuales pre-formateados. Si viene
   * poblado, tiene precedencia sobre la lectura del root. Si no, el
   * front cae a leer los campos del root directamente.
   */
  currentValues?: Record<string, string | number | boolean | null | undefined>;

  /**
   * Mapa de URLs pre-firmadas GET para los documentos actuales del
   * usuario. Si el backend lo pre-firma se respeta; si no, el front
   * lo hidrata con `S3UrlService.getReadUrl()` sobre las keys del root.
   */
  currentDocumentUrls?: Record<string, string | null | undefined>;
}

export interface UserForRequestListResponse {
  data: UserForRequest[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

/**
 * Catálogo de campos solicitables. El backend lo expone como parte de la
 * vista de creación (puede venir embebido en el listado de usuarios o en
 * un endpoint aparte; revisar al cerrar shapes).
 */
export interface RequestableField {
  fieldKey: string;
  label: string;
  fieldType: FieldType;
  /** Si true, sólo aplica a empresas (`tipoUsuarioId = 1`). */
  companyOnly?: boolean;
  /** Si true, sólo aplica si el usuario tiene compliance activo. */
  requiresCompliance?: boolean;
  /** Sugerencia visual de agrupación (personal/document/company/other). */
  group?: 'personal' | 'document' | 'company' | 'other';
}

/**
 * Listado paginado de solicitudes para el dashboard del operador.
 *
 * El backend real usa nombres en español (`agingDias`, `itemsPendientes`,
 * `itemsAprobados`, `createdByCognitoId`). Aceptamos también las
 * variantes en inglés por compatibilidad con iteraciones legacy /
 * documentación previa del plan. Los consumidores usan helpers en el
 * componente (`pendientesOf`, `agingDaysOf`, etc.) para no acoplar el
 * template a un shape específico.
 */
/**
 * Vista compacta de un item dentro del listado (`data[].items`). NO trae
 * `previewUrl`, `submittedValue`, `ocrExtracted` ni `facialVerification`
 * — para eso sigue `GET /data-update-requests/:id`. Sirve para pintar
 * inline qué campos están pendientes por aprobar sin abrir el detalle.
 */
export interface DataUpdateRequestSummaryItem {
  id: string;
  fieldKey: string;
  fieldType: FieldType;
  status: ItemStatus;
  /** El cliente ya envió este dato (SUBMITTED o APPROVED). */
  suministrado: boolean;
  /** `true` sólo si está SUBMITTED — lo que el operador debe revisar. */
  pendientePorAprobar: boolean;
}

export interface DataUpdateRequestSummary {
  id: string;
  usuarioId: number;
  usuarioNombre?: string;
  numeroId?: string;
  estado: RequestEstado;
  vencida: boolean;
  riskReported: boolean;
  rejectCount?: number;

  // ── Contadores de items (español real | inglés legacy) ──
  itemsTotal: number;
  itemsPendientes?: number;
  itemsPending?: number;
  itemsAprobados?: number;

  // ── Cola de revisión (backend nuevo, aditivo/retrocompatible) ──
  // `itemsPendientes` = itemsEsperandoRevision + itemsSinEnviar + itemsRechazados.
  /** Señal principal: hay ≥1 ítem SUBMITTED esperando decisión del operador. */
  requiereRevision?: boolean;
  /** Ítems ya suministrados por el cliente, esperando aprobación (SUBMITTED). */
  itemsEsperandoRevision?: number;
  /** Ítems que el cliente aún no suministró (PENDING). */
  itemsSinEnviar?: number;
  /** Ítems rechazados (REJECTED — persiste sólo en CLOSED_MAX_REJECTS). */
  itemsRechazados?: number;
  /** Detalle compacto por item (sin archivos/OCR — ver detalle completo). */
  items?: DataUpdateRequestSummaryItem[];

  // ── Aging (español real | inglés legacy) ──
  agingDias?: number;
  agingDays?: number;

  deadlineAt: string;
  createdAt: string;
  updatedAt: string;

  // ── Creador (backend devuelve cognitoId; algunos endpoints exponen email) ──
  createdByCognitoId?: string;
  createdByEmail?: string | null;
}

export interface DataUpdateRequestListResponse {
  data: DataUpdateRequestSummary[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export interface DataUpdateRequestListFilters {
  page?: number;
  limit?: number;
  estado?: RequestEstado;
  usuarioId?: number;
  riskReported?: boolean;
  vencida?: boolean;
  tipoUsuarioId?: 0 | 1;
  q?: string;
  from?: string;
  to?: string;
  /**
   * `true` → sólo solicitudes con al menos un ítem suministrado esperando
   * aprobación (la cola de revisión del operador). Se combina con el
   * resto de filtros. Si se omite, comportamiento normal.
   */
  esperandoRevision?: boolean;
}

/**
 * KPIs agregados para el dashboard del operador.
 * `GET /data-update-requests/dashboard` (o equivalente).
 */
export interface DataUpdateRequestKpis {
  pendientes: number;
  vencidas: number;
  esperandoRevision: number;
  rechazadas: number;
  completadas: number;
  reportadas: number;
}

export interface CreateDataUpdateRequestPayload {
  usuarioId: number;
  deadlineAt: string;
  motivo: string;
  items: { fieldKey: string }[];
}

/**
 * Motivo predeterminado para la solicitud (editable por el operador).
 * Texto explícito del plan §2.1. Aplica en individual y masivo.
 */
export const DEFAULT_REQUEST_MOTIVO =
  'Hola 👋 Para mantener tu cuenta activa y poder seguir operando con normalidad, necesitamos que actualices los datos indicados antes de la fecha límite. Es un proceso rápido y seguro; cualquier duda, escríbenos. ¡Gracias por ayudarnos a proteger tu cuenta! 💚';

/**
 * Payload de creación masiva. Plazo y motivo comunes a todos los
 * destinatarios; cada `target` lleva su propio set de `fieldKey`.
 * `POST /data-update-requests/bulk`.
 */
export interface BulkCreateDataUpdateRequestPayload {
  deadlineAt: string;
  motivo: string;
  targets: BulkTarget[];
}

export interface BulkTarget {
  usuarioId: number;
  items: { fieldKey: string }[];
}

/**
 * Respuesta OK de `/bulk`. `creadas` es la cantidad total (debe igualar
 * `targets.length`); `solicitudes` viene populado para que el front
 * pueda enlazar o mostrar resumen.
 */
export interface BulkCreateDataUpdateRequestResponse {
  creadas: number;
  deadlineAt: string;
  solicitudes: DataUpdateRequest[];
}

/**
 * Error de validación todo-o-nada. El backend NO crea ninguna solicitud
 * si al menos un `target` falla. El front muestra los errores por usuario
 * para que el operador corrija sin reiniciar la matriz.
 */
export interface BulkCreateDataUpdateRequestError {
  message: string;
  errores: BulkTargetError[];
}

export interface BulkTargetError {
  usuarioId: number;
  error: string;
}

export interface SubmitItemsPayload {
  items: { itemId: string; value: string | number | boolean }[];
  /**
   * Código OTP de WhatsApp recibido en el teléfono nuevo. Obligatorio si el
   * submit cambia `telefono`/`prefijoTelefono`: lo verifica wallet-api contra
   * el KYC con el mismo número que guarda (el front NO lo verifica antes,
   * porque verificarlo lo consume).
   */
  phoneOtpCode?: string;
}

export interface RejectItemsPayload {
  items: { itemId: string; reason: string }[];
  newDeadlineAt: string;
}

export interface ApproveItemsPayload {
  itemIds: string[];
}
